import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
    Star, ShoppingCart, Minus, Plus, Zap, Shield, Truck,
    RefreshCcw, ChevronRight, CheckCircle2, Heart, Share2,
    ChevronLeft, X, Package, Award, Leaf, IndianRupee, UserCheck, BadgePercent
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import ProductCard from '../components/ProductCard';
import ExpertVisitModal from '../components/ExpertVisitModal';
import { useCart, useWishlist, useAuth, API } from '../App';
import { toast } from 'sonner';

// Calculate subsidy amount based on PM Surya Ghar scheme
const calculateSubsidy = (systemSizeKw) => {
    if (!systemSizeKw || systemSizeKw <= 0) return 0;
    if (systemSizeKw <= 2) return Math.min(systemSizeKw * 30000, 60000);
    if (systemSizeKw <= 3) return 60000 + (systemSizeKw - 2) * 18000;
    return 78000; // Fixed for systems above 3 kW
};

const ProductPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();
    const { user, token } = useAuth();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantity, setQuantity] = useState(1);
    const [selectedImage, setSelectedImage] = useState(0);
    const [showLightbox, setShowLightbox] = useState(false);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [crossSellProducts, setCrossSellProducts] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
    const [submittingReview, setSubmittingReview] = useState(false);
    const [showExpertModal, setShowExpertModal] = useState(false);

    useEffect(() => {
        fetchProduct();
        fetchReviews();
        window.scrollTo(0, 0);
    }, [id]);

    const fetchProduct = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/api/products/${id}`);
            setProduct(response.data);

            // Fetch related products by category
            const relatedRes = await axios.get(`${API}/api/products?category=${response.data.category}&limit=8`);
            setRelatedProducts(relatedRes.data.filter(p => p.id !== id).slice(0, 4));

            // Fetch cross-sell products if defined
            if (response.data.cross_sell_ids?.length > 0) {
                const crossRes = await axios.get(`${API}/api/products`);
                setCrossSellProducts(crossRes.data.filter(p => response.data.cross_sell_ids.includes(p.id)));
            }
        } catch (error) {
            console.log('Using demo data');
            // Demo product with all features
            const demoProduct = {
                id: id,
                name: '5kW Complete Home Solar System',
                brand: 'Luminous',
                category: 'home',
                price: 225000,
                original_price: 275000,
                system_size_kw: 5,
                efficiency_rating: 21.5,
                warranty_years: 25,
                rating: 4.8,
                review_count: 124,
                in_stock: true,
                image_url: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=800',
                gallery_images: [
                    'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800',
                    'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800',
                    'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=800'
                ],
                short_description: 'Complete solar solution for medium-sized homes. Generates up to 25 units of electricity per day.',
                long_description: `This premium 5kW solar system is designed to power medium to large homes efficiently. The system includes high-efficiency monocrystalline panels, a reliable hybrid inverter, and all necessary mounting hardware.

The system can generate approximately 20-25 units (kWh) of electricity per day, which is sufficient for homes with 3-4 ACs, a refrigerator, washing machine, and other regular appliances.

**What's Included:**
- 10 x 500W Monocrystalline Solar Panels
- 1 x 5kW Hybrid Inverter with MPPT
- Complete Mounting Structure
- DC & AC Cables
- Earthing Kit
- Lightning Arrestor
- Professional Installation

**Government Subsidy:**
This system qualifies for the PM Surya Ghar Yojana subsidy of up to ₹78,000 for residential installations.`,
                key_features: [
                    'High-efficiency mono PERC solar cells with 21.5% efficiency',
                    'Hybrid inverter supports battery backup integration',
                    'Weather-resistant aluminum frame with anti-reflective coating',
                    'Real-time monitoring via mobile app',
                    '25-year performance warranty on panels',
                    'Eligible for government subsidy up to ₹78,000'
                ],
                specifications: [
                    { label: 'System Capacity', value: '5 kW' },
                    { label: 'Panel Type', value: 'Mono PERC' },
                    { label: 'Panel Wattage', value: '500W x 10' },
                    { label: 'Inverter Type', value: 'Hybrid MPPT' },
                    { label: 'Daily Generation', value: '20-25 kWh' },
                    { label: 'Monthly Savings', value: '₹3,000-4,000' },
                    { label: 'Roof Area Required', value: '300-350 sq ft' },
                    { label: 'Panel Dimensions', value: '2108 x 1048 x 35mm' },
                    { label: 'Panel Weight', value: '24 kg each' },
                    { label: 'Operating Temperature', value: '-40°C to +85°C' }
                ],
                cross_sell_ids: ['4', '5'],
                related_product_ids: ['1', '3', '6']
            };
            setProduct(demoProduct);

            // Demo related products
            setRelatedProducts([
                { id: '1', name: 'Solar Panel 400W Mono PERC', brand: 'Tata Power Solar', category: 'home', price: 8500, image_url: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400', rating: 4.8 },
                { id: '3', name: '10kW Commercial Solar Array', brand: 'Adani Solar', category: 'commercial', price: 450000, image_url: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=400', rating: 4.7 },
                { id: '6', name: '3kW Home Solar Kit', brand: 'Havells', category: 'home', price: 135000, image_url: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=400', rating: 4.5 }
            ]);

            // Demo cross-sell products
            setCrossSellProducts([
                { id: '4', name: 'Lithium Battery Storage 10kWh', brand: 'Tesla', category: 'battery', price: 95000, image_url: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=400', rating: 4.6 },
                { id: '5', name: 'Hybrid Inverter 5kW', brand: 'Growatt', category: 'inverter', price: 45000, image_url: 'https://images.unsplash.com/photo-1545209463-e2a9e07c8693?w=400', rating: 4.5 }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const fetchReviews = async () => {
        try {
            const response = await axios.get(`${API}/api/products/${id}/reviews`);
            setReviews(response.data);
        } catch (error) {
            // Demo reviews
            setReviews([
                { id: 'r1', user_name: 'Rajesh Kumar', rating: 5, comment: 'Excellent product! Installation was smooth and the system is generating more power than expected. Highly recommended.', created_at: '2024-12-20' },
                { id: 'r2', user_name: 'Priya Sharma', rating: 4, comment: 'Good quality panels. The mobile app for monitoring is very useful. Only minor issue was slight delay in installation.', created_at: '2024-12-15' },
                { id: 'r3', user_name: 'Amit Patel', rating: 5, comment: 'Been using for 6 months now. Electricity bill reduced by 80%. Great investment for the long term.', created_at: '2024-11-28' }
            ]);
        }
    };

    const handleAddToCart = () => {
        if (product) {
            addToCart(product, quantity);
            toast.success(`${product.name} added to cart!`);
        }
    };

    const handleWishlist = () => {
        if (wishlist.some(item => item.id === product.id)) {
            removeFromWishlist(product.id);
            toast.success('Removed from wishlist');
        } else {
            addToWishlist(product);
            toast.success('Added to wishlist');
        }
    };

    const handleShare = async () => {
        try {
            await navigator.share({
                title: product.name,
                text: product.short_description,
                url: window.location.href
            });
        } catch {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Link copied to clipboard!');
        }
    };

    const handleSubmitReview = async () => {
        if (!user) { navigate('/login'); return; }
        if (!newReview.comment.trim()) { toast.error('Please write a review'); return; }
        setSubmittingReview(true);
        try {
            await axios.post(`${API}/api/reviews`, { product_id: id, rating: newReview.rating, comment: newReview.comment },
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success('Review submitted!');
            setNewReview({ rating: 5, comment: '' });
            fetchReviews();
        } catch (error) {
            // Demo mode
            const newR = { id: `r${Date.now()}`, user_name: user?.name || 'You', rating: newReview.rating, comment: newReview.comment, created_at: new Date().toISOString().split('T')[0] };
            setReviews([newR, ...reviews]);
            toast.success('Review submitted!');
            setNewReview({ rating: 5, comment: '' });
        }
        finally { setSubmittingReview(false); }
    };

    // Resolve image URLs - /uploads/ paths need API base URL prepended
    const resolveImageUrl = (url) => {
        if (!url) return null;
        return url.startsWith('/uploads') ? `${API}${url}` : url;
    };

    // Get all images for gallery
    const getAllImages = () => {
        const images = [resolveImageUrl(product.image_url)].filter(Boolean);
        if (product.gallery_images?.length > 0) {
            images.push(...product.gallery_images.filter(img => img).map(resolveImageUrl));
        }
        return images.length > 0 ? images : ['https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800'];
    };

    if (loading) return (
        <div className="min-h-screen pt-14 md:pt-20 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
    );

    if (!product) return null;

    const discount = product.original_price ? Math.round((1 - product.price / product.original_price) * 100) : 0;
    const allImages = getAllImages();
    const isInWishlist = wishlist.some(item => item.id === product.id);

    return (
        <div className="min-h-screen md:pt-20">
            <div className="container-solar -mt-14 pt-14 md:mt-0 md:pt-8">
                {/* Mobile back button */}
                <div className="flex md:hidden items-center gap-2 mb-2">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground">
                        <ChevronLeft className="w-4 h-4" />
                        Back
                    </button>
                </div>

                {/* Breadcrumb — desktop only */}
                <nav className="hidden md:flex items-center gap-2 text-sm text-muted-foreground mb-8">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <ChevronRight className="w-4 h-4" />
                    <Link to="/shop" className="hover:text-primary">Shop</Link>
                    <ChevronRight className="w-4 h-4" />
                    <Link to={`/shop?category=${product.category}`} className="hover:text-primary capitalize">{product.category}</Link>
                    <ChevronRight className="w-4 h-4" />
                    <span className="text-foreground truncate max-w-[200px]">{product.name}</span>
                </nav>

                <div className="grid lg:grid-cols-2 gap-5 md:gap-12 mb-8 md:mb-16">
                    {/* Image Gallery */}
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                        {/* Main Image */}
                        <div
                            className="relative aspect-[4/3] md:aspect-square rounded-xl md:rounded-2xl overflow-hidden bg-secondary/30 cursor-zoom-in mb-3 md:mb-4"
                            onClick={() => setShowLightbox(true)}
                        >
                            <img
                                src={allImages[selectedImage] || 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800'}
                                alt={product.name}
                                className="w-full h-full object-cover transition-transform hover:scale-105"
                            />
                            <div className="absolute top-4 left-4 flex flex-col gap-2">
                                {discount > 0 && <Badge className="badge-sale">-{discount}%</Badge>}
                                {product.efficiency_rating >= 20 && <Badge className="badge-efficiency"><Zap className="w-3 h-3 mr-1" />High Efficiency</Badge>}
                                {!product.in_stock && <Badge className="bg-red-500 text-white">Out of Stock</Badge>}
                            </div>
                        </div>

                        {/* Thumbnail Gallery */}
                        {allImages.length > 1 && (
                            <div className="flex gap-3 overflow-x-auto pb-2">
                                {allImages.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImage(index)}
                                        className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${selectedImage === index ? 'border-primary' : 'border-transparent hover:border-muted-foreground'
                                            }`}
                                    >
                                        <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Product Info */}
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4 md:space-y-6">
                        {/* Category & Brand */}
                        <div className="flex items-center gap-3 text-sm">
                            <Badge variant="outline" className="uppercase">{product.category}</Badge>
                            <Separator orientation="vertical" className="h-4" />
                            <span className="text-primary font-medium">{product.brand}</span>
                        </div>

                        {/* Title */}
                        <h1 className="text-xl md:text-3xl font-bold leading-tight">{product.name}</h1>

                        {/* Rating */}
                        <div className="flex items-center gap-3">
                            <div className="flex">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`w-5 h-5 ${i < Math.floor(product.rating || 4.5) ? 'fill-primary text-primary' : 'text-muted'}`} />
                                ))}
                            </div>
                            <span className="font-medium">{product.rating || 4.5}</span>
                            <span className="text-muted-foreground">({product.review_count || reviews.length} reviews)</span>
                        </div>

                        {/* Short Description */}
                        {product.short_description && (
                            <p className="text-muted-foreground">{product.short_description}</p>
                        )}

                        {/* Price */}
                        {/* Price */}
                        <div className="space-y-4">
                            <div className="flex items-baseline gap-3">
                                <span className="text-3xl font-bold font-mono text-primary">₹{product.price?.toLocaleString()}</span>
                                {product.original_price && (
                                    <span className="text-xl text-muted-foreground line-through font-mono">
                                        ₹{product.original_price?.toLocaleString()}
                                    </span>
                                )}
                                {discount > 0 && (
                                    <Badge className="bg-green-100 text-green-700">Save ₹{(product.original_price - product.price).toLocaleString()}</Badge>
                                )}
                            </div>

                            {product.subsidy_eligible && (
                                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <BadgePercent className="w-5 h-5 text-green-600" />
                                            <span className="font-semibold text-green-800">PM Surya Ghar Subsidy</span>
                                        </div>
                                        <Badge className="bg-green-600 hover:bg-green-700">Eligible Product</Badge>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-green-600 font-medium">Govt. Subsidy Benefit</p>
                                            <p className="text-lg font-bold text-green-700">-₹{calculateSubsidy(product.system_size_kw).toLocaleString()}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-green-600 font-medium">Effective Price</p>
                                            <p className="text-2xl font-bold text-green-800 font-mono">
                                                ₹{(product.price - calculateSubsidy(product.system_size_kw)).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="text-xs text-green-600/80 mt-2">
                                        *Subsidy amount is credited directly to your bank account after installation.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Quick Specs Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-secondary/30 rounded-xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Zap className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">System Size</p>
                                    <p className="font-semibold">{product.system_size_kw} kW</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <RefreshCcw className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Efficiency</p>
                                    <p className="font-semibold">{product.efficiency_rating}%</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Warranty</p>
                                    <p className="font-semibold">{product.warranty_years} Years</p>
                                </div>
                            </div>
                        </div>

                        {/* Key Features */}
                        {product.key_features?.length > 0 && (
                            <div className="space-y-3">
                                <p className="font-semibold">Key Features:</p>
                                <ul className="space-y-2">
                                    {product.key_features.slice(0, 4).map((f, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm">
                                            <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Quantity & Add to Cart */}
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="flex items-center border rounded-lg">
                                <button
                                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                    className="p-3 hover:bg-secondary transition-colors"
                                >
                                    <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-12 text-center font-semibold">{quantity}</span>
                                <button
                                    onClick={() => setQuantity(quantity + 1)}
                                    className="p-3 hover:bg-secondary transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <Button
                                onClick={handleAddToCart}
                                className="btn-primary flex-1"
                                disabled={!product.in_stock}
                            >
                                <ShoppingCart className="w-5 h-5 mr-2" />
                                {product.in_stock ? 'Add to Cart' : 'Out of Stock'}
                            </Button>
                            <Button variant="outline" size="icon" onClick={handleWishlist}>
                                <Heart className={`w-5 h-5 ${isInWishlist ? 'fill-red-500 text-red-500' : ''}`} />
                            </Button>
                            <Button variant="outline" size="icon" onClick={handleShare}>
                                <Share2 className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Request Expert Visit */}
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center shrink-0">
                                    <UserCheck className="w-6 h-6 text-amber-600 dark:text-amber-300" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-amber-800 dark:text-amber-200">Need Expert Guidance?</h3>
                                    <p className="text-sm text-amber-600 dark:text-amber-400">Get a free home assessment from our solar expert</p>
                                </div>
                                <Button
                                    onClick={() => setShowExpertModal(true)}
                                    className="btn-primary"
                                >
                                    Request Visit
                                </Button>
                            </div>
                        </div>

                        {/* Trust Badges */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-accent/10 rounded-xl text-sm">
                            <div className="flex items-center gap-2">
                                <Truck className="w-5 h-5 text-accent" />
                                <span>Free Delivery</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <RefreshCcw className="w-5 h-5 text-accent" />
                                <span>30-Day Returns</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-accent" />
                                <span>Certified Products</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Leaf className="w-5 h-5 text-accent" />
                                <span>Eco-Friendly</span>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Desktop Tabs — hidden on mobile */}
                <Tabs defaultValue="description" className="hidden md:block mb-16">
                    <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent flex-wrap">
                        <TabsTrigger value="description" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary px-6 py-3">
                            Description
                        </TabsTrigger>
                        <TabsTrigger value="specifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary px-6 py-3">
                            Specifications
                        </TabsTrigger>
                        <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary px-6 py-3">
                            Reviews ({reviews.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* Description Tab */}
                    <TabsContent value="description" className="pt-6">
                        <div className="prose prose-green max-w-none">
                            {product.long_description ? (
                                <div className="whitespace-pre-line text-muted-foreground leading-relaxed">
                                    {product.long_description}
                                </div>
                            ) : product.description ? (
                                <p className="text-muted-foreground leading-relaxed">{product.description}</p>
                            ) : (
                                <p className="text-muted-foreground">No description available.</p>
                            )}

                            {/* All Key Features */}
                            {product.key_features?.length > 0 && (
                                <div className="mt-8">
                                    <h3 className="text-lg font-semibold mb-4">All Features</h3>
                                    <ul className="grid md:grid-cols-2 gap-3">
                                        {product.key_features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-2">
                                                <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* Specifications Tab */}
                    <TabsContent value="specifications" className="pt-6">
                        {product.specifications?.length > 0 ? (
                            <div className="bg-card rounded-xl border overflow-hidden">
                                <table className="w-full">
                                    <tbody>
                                        {product.specifications.map((spec, index) => (
                                            <tr key={index} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                                                <td className="px-6 py-4 font-medium text-muted-foreground w-1/3">{spec.label}</td>
                                                <td className="px-6 py-4 font-semibold">{spec.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-muted-foreground">
                                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>No specifications available.</p>
                            </div>
                        )}
                    </TabsContent>

                    {/* Reviews Tab */}
                    <TabsContent value="reviews" className="pt-6">
                        <div className="space-y-8">
                            {/* Write Review */}
                            <div className="bg-secondary/30 rounded-xl p-6">
                                <h3 className="font-semibold mb-4">Write a Review</h3>
                                <div className="flex items-center gap-1 mb-4">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button key={star} onClick={() => setNewReview({ ...newReview, rating: star })}>
                                            <Star className={`w-6 h-6 ${star <= newReview.rating ? 'fill-primary text-primary' : 'text-muted'}`} />
                                        </button>
                                    ))}
                                    <span className="ml-2 text-sm text-muted-foreground">{newReview.rating}/5</span>
                                </div>
                                <Textarea
                                    placeholder="Share your experience with this product..."
                                    value={newReview.comment}
                                    onChange={e => setNewReview({ ...newReview, comment: e.target.value })}
                                    rows={3}
                                    className="mb-4"
                                />
                                <Button onClick={handleSubmitReview} disabled={submittingReview} className="btn-primary">
                                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                                </Button>
                            </div>

                            {/* Reviews List */}
                            <div className="space-y-6">
                                {reviews.length > 0 ? reviews.map(review => (
                                    <div key={review.id} className="border-b pb-6">
                                        <div className="flex items-start gap-4">
                                            <Avatar>
                                                <AvatarFallback className="bg-primary/10 text-primary">
                                                    {review.user_name?.[0] || 'U'}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <p className="font-semibold">{review.user_name}</p>
                                                    <span className="text-xs text-muted-foreground">{review.created_at}</span>
                                                </div>
                                                <div className="flex items-center gap-1 mb-2">
                                                    {[...Array(5)].map((_, i) => (
                                                        <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-primary text-primary' : 'text-muted'}`} />
                                                    ))}
                                                </div>
                                                <p className="text-muted-foreground">{review.comment}</p>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <p className="text-center text-muted-foreground py-8">No reviews yet. Be the first to review!</p>
                                )}
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>

                {/* Mobile: Stacked Sections — visible only on mobile */}
                <div className="md:hidden space-y-6 mb-6">
                    {/* Description */}
                    <section>
                        <h3 className="text-base font-bold mb-2">Description</h3>
                        <div className="text-sm text-muted-foreground leading-relaxed">
                            {product.long_description ? (
                                <div className="whitespace-pre-line line-clamp-6">{product.long_description}</div>
                            ) : product.description ? (
                                <p>{product.description}</p>
                            ) : null}
                        </div>
                    </section>

                    {/* Specifications */}
                    {product.specifications?.length > 0 && (
                        <section>
                            <h3 className="text-base font-bold mb-2">Specifications</h3>
                            <div className="bg-card rounded-lg border overflow-hidden">
                                <table className="w-full text-sm">
                                    <tbody>
                                        {product.specifications.map((spec, index) => (
                                            <tr key={index} className={index % 2 === 0 ? 'bg-muted/30' : ''}>
                                                <td className="px-3 py-2 text-muted-foreground">{spec.label}</td>
                                                <td className="px-3 py-2 font-semibold text-right">{spec.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    )}

                    {/* Reviews */}
                    <section>
                        <h3 className="text-base font-bold mb-2">Reviews ({reviews.length})</h3>
                        <div className="space-y-3">
                            {reviews.length > 0 ? reviews.slice(0, 3).map(review => (
                                <div key={review.id} className="p-3 bg-secondary/30 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                            {review.user_name?.[0] || 'U'}
                                        </div>
                                        <span className="font-semibold text-sm">{review.user_name}</span>
                                        <div className="flex items-center gap-0.5 ml-auto">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'fill-primary text-primary' : 'text-muted'}`} />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2">{review.comment}</p>
                                </div>
                            )) : (
                                <p className="text-sm text-muted-foreground">No reviews yet.</p>
                            )}
                            <div className="p-3 border rounded-lg">
                                <div className="flex items-center gap-1 mb-2">
                                    {[1, 2, 3, 4, 5].map(star => (
                                        <button key={star} onClick={() => setNewReview({ ...newReview, rating: star })}>
                                            <Star className={`w-5 h-5 ${star <= newReview.rating ? 'fill-primary text-primary' : 'text-muted'}`} />
                                        </button>
                                    ))}
                                </div>
                                <Textarea
                                    placeholder="Write a review..."
                                    value={newReview.comment}
                                    onChange={e => setNewReview({ ...newReview, comment: e.target.value })}
                                    rows={2}
                                    className="mb-2 text-sm"
                                />
                                <Button onClick={handleSubmitReview} disabled={submittingReview} size="sm" className="btn-primary w-full">
                                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Cross-sell Products */}
                {crossSellProducts.length > 0 && (
                    <section className="mb-6 md:mb-16">
                        <h2 className="text-base md:text-xl font-bold mb-3 md:mb-8">Frequently Bought Together</h2>
                        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {crossSellProducts.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                        <div className="flex md:hidden gap-3 overflow-x-auto pb-2 -mx-4 px-4 product-slider">
                            {crossSellProducts.map((p) => (
                                <div key={p.id} className="product-mini-card" onClick={() => navigate(`/products/${p.id}`)}>
                                    <img src={p.image_url} alt={p.name} className="product-mini-img" />
                                    <div className="product-mini-info">
                                        <p className="product-mini-name">{p.name}</p>
                                        <p className="product-mini-price">₹{p.price?.toLocaleString()}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Related Products */}
                {relatedProducts.length > 0 && (
                    <section className="mb-6 md:mb-0">
                        <h2 className="text-base md:text-xl font-bold mb-3 md:mb-8">Related Products</h2>
                        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {relatedProducts.slice(0, 4).map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                        <div className="flex md:hidden gap-3 overflow-x-auto pb-2 -mx-4 px-4 product-slider">
                            {relatedProducts.slice(0, 6).map((p) => (
                                <div key={p.id} className="product-mini-card" onClick={() => navigate(`/products/${p.id}`)}>
                                    <img src={p.image_url} alt={p.name} className="product-mini-img" />
                                    <div className="product-mini-info">
                                        <p className="product-mini-name">{p.name}</p>
                                        <p className="product-mini-price">₹{p.price?.toLocaleString()}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            {/* Image Lightbox */}
            <AnimatePresence>
                {showLightbox && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
                        onClick={() => setShowLightbox(false)}
                    >
                        <button
                            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
                            onClick={() => setShowLightbox(false)}
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {allImages.length > 1 && (
                            <>
                                <button
                                    className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-full"
                                    onClick={(e) => { e.stopPropagation(); setSelectedImage((selectedImage - 1 + allImages.length) % allImages.length); }}
                                >
                                    <ChevronLeft className="w-8 h-8" />
                                </button>
                                <button
                                    className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-full"
                                    onClick={(e) => { e.stopPropagation(); setSelectedImage((selectedImage + 1) % allImages.length); }}
                                >
                                    <ChevronRight className="w-8 h-8" />
                                </button>
                            </>
                        )}

                        <motion.img
                            key={selectedImage}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            src={allImages[selectedImage]}
                            alt={product.name}
                            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                        />

                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
                            {selectedImage + 1} / {allImages.length}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Expert Visit Modal */}
            <ExpertVisitModal
                isOpen={showExpertModal}
                onClose={() => setShowExpertModal(false)}
                productId={product?.id}
                productName={product?.name}
            />
        </div>
    );
};

export default ProductPage;
